import * as React from 'react';
import type { JSX } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const headingVariants = cva('font-semibold', {
  variants: {
    size: {
      '4xl': 'text-2xl md:text-3xl lg:text-4xl',
      '3xl': 'text-xl md:text-2xl lg:text-3xl',
      '2xl': 'text-xl md:text-2xl',
      xl: 'text-lg md:text-xl',
      lg: 'text-base md:text-lg',
      default: 'text-sm md:text-base',
      sm: 'text-xs: md:text-sm',
      xs: 'text-xs',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
  },
  defaultVariants: {
    size: 'default',
    align: 'left',
  },
});

export interface HeadingProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof headingVariants> {
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  as?: React.ElementType;
  color?: string;
}

function Heading({
  level = 1,
  as,
  className,
  size,
  align = 'left',
  style,
  color,
  children,
  ...rest
}: HeadingProps) {
  const Tag = as || (`h${level}` as keyof JSX.IntrinsicElements);
  const mergedStyle = {
    ...style,
    ...(color && { color }),
  };

  return (
    <Tag
      className={cn(headingVariants({ size, align }), className)}
      style={mergedStyle}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export { Heading };
